import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { PAGE_COPY } from '../copy';
import { useMatchingCount } from '../hooks/useNotificationsPage';
import type { InboxQuery } from '../pageFilter';

/**
 * The confirmation before a whole-filter action (spec 147 FR-004): it names
 * the count the service gives for the filter, whatever is loaded, and
 * confirms only once that count is known.
 */
export function FilterActionDialog({
  action,
  query,
  onOpenChange,
  onConfirm,
}: {
  /** The action awaiting confirmation; `null` when the dialog is closed. */
  action: 'read' | 'archive' | null;
  query: InboxQuery;
  onOpenChange: (open: boolean) => void;
  onConfirm: (action: 'read' | 'archive') => void;
}) {
  const count = useMatchingCount(query, action !== null);
  const total = count.total;
  const title =
    total === undefined
      ? count.loadFailed
        ? 'The count could not be loaded.'
        : 'Counting…'
      : action === 'archive'
        ? PAGE_COPY.confirmArchive(total)
        : PAGE_COPY.confirmRead(total);

  return (
    <Dialog open={action !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {action === 'archive'
              ? 'Every notification these filters match, loaded or not. Archiving also marks them read.'
              : 'Every notification these filters match, loaded or not.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>
            Cancel
          </DialogClose>
          <Button
            disabled={!total}
            onClick={() => {
              if (action) onConfirm(action);
              onOpenChange(false);
            }}
          >
            {action === 'archive' ? PAGE_COPY.archive : PAGE_COPY.markRead}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
